// Global Instructions Rule Applied!

/**
 * useInputManager Hook
 *
 * This hook manages the global editing state for the InputManager component system.
 * It prevents multiple fields from being edited simultaneously and handles state transitions.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { UseInputManagerReturn } from '../types'

// ============================================================================
// Global State Management
// ============================================================================

/**
 * Global state to track which field is currently being edited
 * This prevents multiple fields from being edited simultaneously
 */
let globalEditingFieldId: string | null = null

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for managing global editing state in InputManager
 * @returns Object with editing state and control functions
 */
export const useInputManager = (): UseInputManagerReturn => {
  const [editingFieldId, setEditingFieldId] = useState<string | null>(globalEditingFieldId)
  const fieldIdRef = useRef<string | null>(null)

  // Sync local state with global state
  useEffect(() => {
    setEditingFieldId(globalEditingFieldId)
  }, [])

  /**
   * Start editing a field
   * @param fieldId - ID of the field to start editing
   * @returns True if editing started successfully, false if another field is being edited
   */
  const startEdit = useCallback((fieldId: string): boolean => {
    // If another field is already being edited, prevent starting edit
    if (globalEditingFieldId && globalEditingFieldId !== fieldId) {
      return false
    }

    // Set global editing state
    globalEditingFieldId = fieldId
    setEditingFieldId(fieldId)
    fieldIdRef.current = fieldId

    return true
  }, [])

  /**
   * Stop editing the current field
   *
   * ALWAYS pass fieldId. `globalEditingFieldId` is module-global — shared by
   * every InputManager instance in the app — so a bare stopEdit() clears a
   * token another instance owns. Each instance's `editingFieldId` state is
   * its own, so the robbed instance does not re-render and its field keeps
   * rendering as an open input until some unrelated re-render makes
   * shouldFieldBeEditing() re-read the now-null global and collapse it.
   *
   * @param fieldId - ID of the field to stop editing (optional, for validation)
   */
  const stopEdit = useCallback((fieldId?: string): void => {
    // If fieldId is provided, only stop if it matches the current editing field
    if (fieldId && globalEditingFieldId !== fieldId) {
      return
    }

    // Clear global editing state
    globalEditingFieldId = null
    setEditingFieldId(null)
    fieldIdRef.current = null
  }, [])

  /**
   * Check if a specific field is currently being edited
   * @param fieldId - ID of the field to check
   * @returns True if the field is being edited
   */
  const isFieldEditing = useCallback((fieldId: string): boolean => {
    return globalEditingFieldId === fieldId
  }, [])

  /**
   * Check if any field is currently being edited
   * @returns True if any field is being edited
   */
  const isAnyFieldEditing = useCallback((): boolean => {
    return globalEditingFieldId !== null
  }, [])

  return {
    editingFieldId,
    startEdit,
    stopEdit,
    isFieldEditing,
    isAnyFieldEditing: isAnyFieldEditing()
  }
}

// ============================================================================
// Field-Specific Hook
// ============================================================================

/**
 * Hook for managing individual field editing state
 * @param fieldKey - Key of the field
 * @returns Object with field-specific editing state and functions
 */
export const useFieldEditing = (fieldKey: string) => {
  const { startEdit, stopEdit, isFieldEditing, isAnyFieldEditing } = useInputManager()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [tempValue, setTempValue] = useState<any>(null)
  const [showPopconfirm, setShowPopconfirm] = useState<boolean>(false)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  // Use fieldKey directly as fieldId to match InputManager's usage
  const fieldId = useRef<string>(fieldKey).current

  // Update local editing state when global state changes
  useEffect(() => {
    const isCurrentlyEditing = isFieldEditing(fieldId)
    setIsEditing(isCurrentlyEditing)

    if (!isCurrentlyEditing) {
      // Reset field state when editing stops
      setShowPopconfirm(false)
      setHasChanges(false)
    }
  }, [isFieldEditing, fieldId])

  /**
   * Start editing this field
   * @param initialValue - Initial value for the field
   * @returns True if editing started successfully
   */
  const startFieldEdit = useCallback(
    (initialValue: any): boolean => {
      const success = startEdit(fieldId)
      if (success) {
        setTempValue(initialValue)
        setHasChanges(false)
        setShowPopconfirm(false)
      }
      return success
    },
    [startEdit, fieldId]
  )

  /**
   * Stop editing this field
   */
  const stopFieldEdit = useCallback((): void => {
    stopEdit(fieldId)
  }, [stopEdit, fieldId])

  /**
   * Update temporary value and track changes
   * @param newValue - New temporary value
   * @param originalValue - Original value for comparison
   */
  const updateTempValue = useCallback((newValue: any, originalValue: any): void => {
    setTempValue(newValue)
    setHasChanges(newValue !== originalValue)
  }, [])

  /**
   * Show popconfirm for unsaved changes
   */
  const showUnsavedChangesConfirm = useCallback((): void => {
    if (hasChanges) {
      setShowPopconfirm(true)
    }
  }, [hasChanges])

  /**
   * Hide popconfirm
   */
  const hidePopconfirm = useCallback((): void => {
    setShowPopconfirm(false)
  }, [])

  /**
   * Reset field to original state
   * @param originalValue - Original value to reset to
   */
  const resetField = useCallback((originalValue: any): void => {
    setTempValue(originalValue)
    setHasChanges(false)
    setShowPopconfirm(false)
  }, [])

  return {
    fieldId,
    isEditing,
    tempValue,
    showPopconfirm,
    hasChanges,
    isAnyFieldEditing: isAnyFieldEditing,
    startFieldEdit,
    stopFieldEdit,
    updateTempValue,
    showUnsavedChangesConfirm,
    hidePopconfirm,
    resetField
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the currently editing field ID
 * @returns Currently editing field ID or null
 */
const getCurrentEditingFieldId = (): string | null => {
  return globalEditingFieldId
}

/**
 * Check if a field can start editing (no other field is being edited)
 * @param fieldId - ID of the field to check
 * @returns True if the field can start editing
 */
const canStartEditing = (fieldId: string): boolean => {
  return !globalEditingFieldId || globalEditingFieldId === fieldId
}

/**
 * Force stop all editing (emergency cleanup)
 * This should be used sparingly, typically for cleanup on component unmount
 */
const forceStopAllEditing = (): void => {
  globalEditingFieldId = null
}

/**
 * Get editing state summary
 * @returns Object with current editing state information
 */
const getEditingStateSummary = () => {
  return {
    isAnyFieldEditing: globalEditingFieldId !== null,
    currentEditingFieldId: globalEditingFieldId
  }
}
